import { collectFileGarbage } from "./downloads/service";
import { moveRedirect, freeRedirect } from "../blog/redirects";
import { z } from "zod";
import { canonicalJson, digest, hashBytes } from "../../canonical";
import { hasFeature } from "../../features";
import { UPLOAD_PREFIX } from "../../drafts";
import { photoDimensions } from "../../photoSizes";
import { mapSite } from "../../postgres";
import { MAX_SHOP_FILES, SHOP_FILE_CHUNK_BYTES, CustomizationSetInputSchema, ProductChangeSchema, ProductDetailsSchema, ProductRevisionSchema, ShopCategoryInputSchema, ShopFileInputSchema, ShopIdSchema, ShopOptionsSchema, ShopSettingsSchema, ShopStockInputSchema, ShopVariantSchema } from "./schema";
import { productCopy, variantKey, variantsFromOptions } from "./state";
import { ShopStore } from "./store";
export const SHOP_UNAVAILABLE = "The shop isn't available on this site.";
export class ShopUnavailable extends Error {
    name = "ShopUnavailable";
    constructor() { super(SHOP_UNAVAILABLE); }
}
export class ShopRefusal extends Error {
    name = "ShopRefusal";
}
export class ShopConflict extends Error {
    name = "ShopConflict";
    constructor() { super("This changed somewhere else. Reload it and try again."); }
}
const json = (value) => value === null ? null : JSON.stringify(value);
const same = (a, b) => canonicalJson(a) === canonicalJson(b);
const versionSchema = z.number().int().positive();
function parse(schema, input) {
    const result = schema.safeParse(input);
    if (!result.success)
        throw new ShopRefusal(result.error.issues[0]?.message ?? "Check the shop details");
    return result.data;
}
function expectVersion(expected, actual) { parse(versionSchema, expected); if (expected !== actual)
    throw new ShopConflict(); }
/** Direct catalogue saves, always owned, gated and serialized with the site's row. Nothing writes a Website snapshot. */
export class ShopService {
    db;
    objects;
    constructor(db, objects) {
        this.db = db;
        this.objects = objects;
    }
    inSite(siteId, actor, work) {
        return this.db.withSite(siteId, actor, async (client, site) => {
            if (!hasFeature(site, "shop"))
                throw new ShopUnavailable();
            return work(client, site);
        });
    }
    /** Owned reads use a consistent snapshot, without running the mutation policy. */
    readSite(siteId, actor, work) {
        return this.db.transaction(async (client) => {
            await client.query("SET TRANSACTION ISOLATION LEVEL REPEATABLE READ READ ONLY");
            const found = await client.query("SELECT * FROM builder_sites WHERE id=$1 AND owner_id=$2", [siteId, actor.userId]);
            if (!found.rows[0])
                throw new ShopRefusal("Site not found for actor");
            const site = mapSite(found.rows[0]);
            if (!hasFeature(site, "shop"))
                throw new ShopUnavailable();
            return work(client, site);
        });
    }
    read(siteId, actor) { return this.readSite(siteId, actor, (client) => new ShopStore(client).load(siteId)); }
    readProduct(siteId, actor, id) { return this.readSite(siteId, actor, (client) => new ShopStore(client).product(siteId, parse(ShopIdSchema, id))); }
    history(siteId, actor, id) { return this.readSite(siteId, actor, async (client) => { await this.product(client, siteId, id); return new ShopStore(client).history(siteId, id); }); }
    photo(siteId, actor, productId, assetId, version) {
        return this.readSite(siteId, actor, async (client) => { await this.product(client, siteId, productId); return new ShopStore(client).asset(siteId, productId, parse(ShopIdSchema, assetId), parse(versionSchema, version)); });
    }
    revision(siteId, actor, hash) {
        return this.readSite(siteId, actor, async (client) => {
            parse(z.string().regex(/^[a-f0-9]{64}$/), hash);
            const found = await client.query("SELECT description,seo FROM builder_product_revisions WHERE site_id=$1 AND digest=$2", [siteId, hash]);
            const revision = found.rows[0] ? ProductRevisionSchema.parse(found.rows[0]) : null;
            if (revision && await digest(revision) !== hash)
                throw new Error("Product revision integrity check failed");
            return revision;
        });
    }
    async saveRevision(siteId, actor, input) {
        return this.inSite(siteId, actor, async (client) => {
            await this.settings(client, siteId);
            const revision = parse(ProductRevisionSchema, input);
            return { digest: await this.insertRevision(client, siteId, revision) };
        });
    }
    /** rowVersion=0 creates settings; subsequent changes use the settings row's concurrency version. */
    async saveSettings(siteId, actor, input, rowVersion) {
        return this.inSite(siteId, actor, async (client, site) => {
            const next = parse(ShopSettingsSchema, input);
            const found = await client.query("SELECT settings,row_version FROM builder_shop WHERE site_id=$1", [siteId]);
            const previous = found.rows[0];
            if (previous)
                expectVersion(rowVersion, previous.row_version);
            else if (rowVersion !== 0)
                throw new ShopConflict();
            if (previous && same(next, previous.settings))
                return previous.settings;
            if (next.name !== previous?.settings.name && /stack\.je|stackje/i.test(next.name))
                throw new ShopRefusal("Choose a name that's your shop's own.");
            if (previous && next.currency !== previous.settings.currency) {
                if ((await client.query("SELECT 1 FROM builder_shop_orders WHERE site_id=$1 LIMIT 1", [siteId])).rowCount)
                    throw new ShopRefusal("Currency can't change after the first order.");
                await client.query("UPDATE builder_product_variants SET data=jsonb_set(data,'{currency}',to_jsonb($2::text)),row_version=row_version+1 WHERE site_id=$1", [siteId, next.currency]);
                await client.query("UPDATE builder_shop_customization_sets SET data=jsonb_set(data,'{currency}',to_jsonb($2::text)),row_version=row_version+1 WHERE site_id=$1", [siteId, next.currency]);
            }
            if (next.base !== previous?.settings.base) {
                const snapshot = await this.db.loadBundleSnapshot(siteId, site.draftId, client);
                const blog = await client.query("SELECT settings FROM builder_blog WHERE site_id=$1", [siteId]);
                if (Object.keys(snapshot.manifest.routes).some((path) => path === next.base || path.startsWith(`${next.base}/`)) || blog.rows[0]?.settings.base === next.base)
                    throw new ShopRefusal("A page or another module already uses that address");
            }
            await client.query(`INSERT INTO builder_shop(site_id,settings) VALUES($1,$2::jsonb) ON CONFLICT(site_id)
        DO UPDATE SET settings=EXCLUDED.settings,version=builder_shop.version+1,row_version=builder_shop.row_version+1,updated_at=now()`, [siteId, json(next)]);
            return next;
        });
    }
    async createProduct(siteId, actor, input) {
        return this.inSite(siteId, actor, async (client) => {
            const parsed = parse(z.object({ id: ShopIdSchema, name: ProductDetailsSchema.shape.name, slug: ProductDetailsSchema.shape.slug, price: ProductDetailsSchema.shape.price.default(null), revision: ProductDetailsSchema.shape.revision.optional() }).strict(), input);
            const settings = await this.settings(client, siteId);
            if ((await client.query("SELECT 1 FROM builder_products WHERE site_id=$1 AND id=$2", [siteId, parsed.id])).rowCount)
                throw new ShopRefusal("Product id already exists");
            const details = { name: parsed.name, slug: parsed.slug, categories: [], photos: [], revision: parsed.revision ?? await this.insertRevision(client, siteId, { description: "", seo: {} }), price: parsed.price, physical: true, downloads: false, options: [], files: [], customizationSetIds: [], textFields: [] };
            if (parsed.price === 0)
                throw new ShopRefusal("Free prices are only allowed on download-only products. Set download-only fulfilment before entering 0.");
            await this.assertDetails(client, siteId, parsed.id, details);
            await client.query("INSERT INTO builder_products(site_id,id,details,slug) VALUES($1,$2,$3::jsonb,$4)", [siteId, parsed.id, json(details), details.slug]);
            const variants = variantsFromOptions([], [], settings.currency, this.newVariantId, parsed.price);
            await this.writeVariants(client, siteId, parsed.id, variants);
            const product = await this.product(client, siteId, parsed.id);
            await this.log(client, siteId, product.id, "create", actor, null, productCopy(product));
            return product;
        });
    }
    async saveProduct(siteId, actor, write, change) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            const details = parse(ProductDetailsSchema, { ...product.details, ...parse(ProductChangeSchema, change) });
            const next = { ...productCopy(product), details, variants: productCopy(product).variants.map((variant) => !details.options.length && "price" in change ? { ...variant, price: details.price } : variant) };
            return this.saveCopy(client, siteId, actor, product, next, "edit");
        });
    }
    /** Stax text is saved atomically to the shown revision. A retried turn cannot apply it twice. */
    async saveProductText(siteId, actor, write, expectedRevision, set, actionId) {
        return this.inSite(siteId, actor, async (client) => {
            parse(ShopIdSchema, actionId);
            const action = `stax:${actionId}`;
            const history = await client.query("SELECT 1 FROM builder_product_changes WHERE site_id=$1 AND product_id=$2 AND action=$3", [siteId, write.id, action]);
            if (history.rowCount)
                return this.product(client, siteId, write.id);
            const product = await this.lock(client, siteId, write);
            if (product.details.revision !== expectedRevision)
                throw new ShopConflict();
            const text = parse(z.object({ name: ProductDetailsSchema.shape.name, revision: ProductDetailsSchema.shape.revision }).strict(), set);
            const details = parse(ProductDetailsSchema, { ...product.details, ...text });
            return this.saveCopy(client, siteId, actor, product, { ...productCopy(product), details }, action);
        });
    }
    /** Whole option changes create combinations atomically; retired variants and their stock stay stored for history/orders. */
    async saveOptions(siteId, actor, write, input) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            const options = parse(ShopOptionsSchema, input);
            const settings = await this.settings(client, siteId);
            const stored = await client.query("SELECT data FROM builder_product_variants WHERE site_id=$1 AND product_id=$2", [siteId, product.id]);
            const variants = variantsFromOptions(options, stored.rows.map((row) => row.data), settings.currency, this.newVariantId, product.details.price);
            return this.saveCopy(client, siteId, actor, product, { ...productCopy(product), details: { ...product.details, options }, variants }, "options");
        });
    }
    /** Whole-variant/price updates: no partial batch becomes visible. Option identities can only change through saveOptions. */
    async saveVariants(siteId, actor, write, input) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            const variants = parse(z.array(ShopVariantSchema).min(1).max(256), input);
            const current = new Map(product.variants.map((variant) => [variant.id, variantKey(variant.values)]));
            if (variants.length !== current.size || new Set(variants.map((variant) => variant.id)).size !== current.size || variants.some((variant) => current.get(variant.id) !== variantKey(variant.values)))
                throw new ShopRefusal("Save options separately before changing variant identities");
            return this.saveCopy(client, siteId, actor, product, { ...productCopy(product), details: { ...product.details, ...(!product.details.options.length ? { price: variants[0].price } : {}) }, variants }, "variants");
        });
    }
    /** Separate, confirmed by the caller. A details save cannot turn selling on. */
    async setAvailability(siteId, actor, write, availability) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            return this.saveCopy(client, siteId, actor, product, { ...productCopy(product), availability: parse(z.enum(["hidden", "available"]), availability) }, "availability");
        });
    }
    async deleteProduct(siteId, actor, write) {
        await this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            await client.query("UPDATE builder_products SET deleted=true,availability='hidden',row_version=row_version+1,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, product.id]);
            await this.log(client, siteId, product.id, "delete", actor, productCopy(product), null);
            if (product.availability === "available")
                await this.bump(client, siteId);
        });
    }
    /** Restore details/variants only. Visibility is deliberate and inventory never enters this path. */
    async restoreProduct(siteId, actor, write, seq) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            const history = await new ShopStore(client).history(siteId, product.id);
            const selected = history.find((entry) => entry.seq === parse(versionSchema, seq));
            if (!selected?.after)
                throw new ShopRefusal("That product version isn't available");
            return this.saveCopy(client, siteId, actor, product, await this.restoreCopy(client, siteId, product, selected.after), "restore");
        });
    }
    async undoProduct(siteId, actor, write) {
        return this.inSite(siteId, actor, async (client) => {
            const product = await this.lock(client, siteId, write);
            const rows = await new ShopStore(client).history(siteId, product.id);
            const undone = new Set(rows.flatMap((entry) => entry.undoes ?? []));
            const selected = rows.find((entry) => entry.before && !entry.undoes && !undone.has(entry.seq) && entry.action !== "availability");
            if (!selected?.before)
                throw new ShopRefusal("There's nothing to undo");
            return this.saveCopy(client, siteId, actor, product, await this.restoreCopy(client, siteId, product, selected.before), "undo", [selected.seq]);
        });
    }
    async saveStock(siteId, actor, variantId, rowVersion, input) {
        return this.inSite(siteId, actor, async (client) => {
            const next = parse(ShopStockInputSchema, input);
            parse(ShopIdSchema, variantId);
            const found = await client.query(`SELECT s.* FROM builder_variant_stock s JOIN builder_product_variants v ON v.site_id=s.site_id AND v.id=s.variant_id
        JOIN builder_products p ON p.site_id=v.site_id AND p.id=v.product_id WHERE s.site_id=$1 AND s.variant_id=$2 AND v.active AND NOT p.deleted FOR UPDATE OF s`, [siteId, variantId]);
            const stock = found.rows[0];
            if (!stock)
                throw new ShopRefusal("Variant does not exist");
            expectVersion(rowVersion, stock.row_version);
            if (next.tracked === stock.tracked && next.quantity === Number(stock.quantity))
                return { variantId, ...next, rowVersion };
            const saved = await client.query("UPDATE builder_variant_stock SET tracked=$3,quantity=$4,row_version=row_version+1 WHERE site_id=$1 AND variant_id=$2 RETURNING row_version", [siteId, variantId, next.tracked, next.quantity]);
            await this.bump(client, siteId);
            return { variantId, ...next, rowVersion: saved.rows[0].row_version };
        });
    }
    async saveCategory(siteId, actor, id, rowVersion, input) {
        return this.inSite(siteId, actor, async (client) => {
            const settings = await this.settings(client, siteId);
            parse(ShopIdSchema, id);
            const data = parse(ShopCategoryInputSchema, input);
            const found = await client.query("SELECT data,row_version FROM builder_shop_categories WHERE site_id=$1 AND id=$2", [siteId, id]);
            if (found.rows[0])
                expectVersion(rowVersion, found.rows[0].row_version);
            else if (rowVersion !== 0)
                throw new ShopConflict();
            if ((await client.query("SELECT 1 FROM builder_shop_categories WHERE site_id=$1 AND slug=$2 AND id<>$3", [siteId, data.slug, id])).rowCount)
                throw new ShopRefusal("Another category uses that address");
            const categories = await client.query("SELECT id,parent_id FROM builder_shop_categories WHERE site_id=$1", [siteId]);
            const parents = new Map(categories.rows.map((row) => [row.id, row.parent_id]));
            if (data.parentId && !parents.has(data.parentId))
                throw new ShopRefusal("Parent category does not exist");
            let parent = data.parentId;
            const seen = new Set([id]);
            while (parent) {
                if (seen.has(parent))
                    throw new ShopRefusal("Categories cannot contain a cycle");
                seen.add(parent);
                parent = parents.get(parent) ?? null;
            }
            if (found.rows[0] && same(data, found.rows[0].data))
                return { id, ...data, rowVersion };
            const saved = await client.query(`INSERT INTO builder_shop_categories(site_id,id,data,slug,parent_id) VALUES($1,$2,$3::jsonb,$4,$5)
        ON CONFLICT(site_id,id) DO UPDATE SET data=EXCLUDED.data,slug=EXCLUDED.slug,parent_id=EXCLUDED.parent_id,row_version=builder_shop_categories.row_version+1 RETURNING row_version`, [siteId, id, json(data), data.slug, data.parentId]);
            if (found.rows[0] && found.rows[0].data.slug !== data.slug)
                await this.redirect(client, siteId, `${settings.base}/c/${found.rows[0].data.slug}`, `${settings.base}/c/${data.slug}`);
            await this.redirect(client, siteId, `${settings.base}/c/${data.slug}`);
            await this.bump(client, siteId);
            return { id, ...data, rowVersion: saved.rows[0].row_version };
        });
    }
    async deleteCategory(siteId, actor, id, rowVersion) {
        await this.inSite(siteId, actor, async (client) => {
            await this.settings(client, siteId);
            const found = await client.query("SELECT row_version FROM builder_shop_categories WHERE site_id=$1 AND id=$2", [siteId, id]);
            if (!found.rows[0])
                throw new ShopRefusal("Category does not exist");
            expectVersion(rowVersion, found.rows[0].row_version);
            if ((await client.query("SELECT 1 FROM builder_shop_categories WHERE site_id=$1 AND parent_id=$2", [siteId, id])).rowCount)
                throw new ShopRefusal("Move or remove the subcategories before deleting this category");
            const products = await client.query("SELECT id FROM builder_products WHERE site_id=$1 AND NOT deleted AND details->'categories' ? $2", [siteId, id]);
            for (const row of products.rows) {
                const product = await this.product(client, siteId, row.id);
                await this.saveCopy(client, siteId, actor, product, { ...productCopy(product), details: { ...product.details, categories: product.details.categories.filter((category) => category !== id) } }, "category-remove");
            }
            await client.query("DELETE FROM builder_shop_categories WHERE site_id=$1 AND id=$2", [siteId, id]);
            await this.bump(client, siteId);
        });
    }
    async saveCustomizationSet(siteId, actor, id, rowVersion, input) {
        return this.inSite(siteId, actor, async (client) => {
            const settings = await this.settings(client, siteId);
            parse(ShopIdSchema, id);
            const data = parse(CustomizationSetInputSchema, input);
            if (data.currency !== settings.currency)
                throw new ShopRefusal("Use the shop currency for customization prices");
            const found = await client.query("SELECT data,row_version FROM builder_shop_customization_sets WHERE site_id=$1 AND id=$2", [siteId, id]);
            if (found.rows[0])
                expectVersion(rowVersion, found.rows[0].row_version);
            else if (rowVersion !== 0)
                throw new ShopConflict();
            if (found.rows[0] && same(data, found.rows[0].data))
                return { id, ...data, rowVersion };
            const saved = await client.query(`INSERT INTO builder_shop_customization_sets(site_id,id,data) VALUES($1,$2,$3::jsonb)
        ON CONFLICT(site_id,id) DO UPDATE SET data=EXCLUDED.data,row_version=builder_shop_customization_sets.row_version+1 RETURNING row_version`, [siteId, id, json(data)]);
            await this.bump(client, siteId);
            return { id, ...data, rowVersion: saved.rows[0].row_version };
        });
    }
    async deleteCustomizationSet(siteId, actor, id, rowVersion) {
        await this.inSite(siteId, actor, async (client) => {
            const found = await client.query("SELECT row_version FROM builder_shop_customization_sets WHERE site_id=$1 AND id=$2", [siteId, id]);
            if (!found.rows[0])
                throw new ShopRefusal("Customization set does not exist");
            expectVersion(rowVersion, found.rows[0].row_version);
            if ((await client.query("SELECT 1 FROM builder_products WHERE site_id=$1 AND NOT deleted AND details->'customizationSetIds' ? $2", [siteId, id])).rowCount)
                throw new ShopRefusal("Remove this set from its products before deleting it");
            await client.query("DELETE FROM builder_shop_customization_sets WHERE site_id=$1 AND id=$2", [siteId, id]);
            await this.bump(client, siteId);
        });
    }
    /** Copies a site's media reference/alt or verifies an existing upload. It never creates a Website media record. */
    async registerPhoto(siteId, actor, productId, input) {
        return this.inSite(siteId, actor, async (client, site) => {
            await this.product(client, siteId, productId);
            if (!this.objects)
                throw new ShopRefusal("Photo storage is unavailable");
            let asset;
            let alt;
            if ("photoId" in input) {
                const record = (await this.db.loadBundleSnapshot(siteId, site.draftId, client)).content.recordsById[parse(ShopIdSchema, input.photoId)];
                if (record?.kind !== "media")
                    throw new ShopRefusal("Website photo does not exist");
                asset = record.fields.asset;
                alt = record.fields.alt;
            }
            else
                asset = input.asset;
            const extensions = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" };
            const extension = extensions[asset?.mimeType];
            if (!extension || !/^[a-f0-9]{64}$/.test(asset.sha256) || asset.version !== 1 || asset.assetId !== `photo-${asset.sha256.slice(0, 24)}` || asset.objectKey !== `${UPLOAD_PREFIX}${asset.sha256}.${extension}`)
                throw new ShopRefusal("Choose a JPEG, PNG or WebP upload for the product");
            const stored = await this.objects.get(`originals/${siteId}/${asset.objectKey}`);
            if (!stored || stored.contentType !== asset.mimeType || await hashBytes(stored.body) !== asset.sha256)
                throw new ShopRefusal("Photo upload does not exist");
            const size = photoDimensions(stored.body, asset.mimeType);
            await client.query(`INSERT INTO builder_product_assets(site_id,product_id,asset_id,version,object_key,mime_type,sha256,width,height)
        VALUES($1,$2,$3,$4,$5,$6,$7,$8,$9) ON CONFLICT DO NOTHING`, [siteId, productId, asset.assetId, asset.version, asset.objectKey, asset.mimeType, asset.sha256, size?.width ?? null, size?.height ?? null]);
            return { photo: { assetId: asset.assetId, version: asset.version, ...(alt !== undefined ? { alt } : {}) } };
        });
    }
    fileStorage() {
        if (!this.objects?.privateFiles)
            throw new ShopRefusal("Private multipart file storage is unavailable");
        return this.objects.privateFiles;
    }
    async reserveFile(client, siteId, productId, id) {
        const slots = await client.query("SELECT id FROM builder_product_file_slots WHERE site_id=$1 AND product_id=$2", [siteId, productId]);
        if (!slots.rows.some((row) => row.id === id) && slots.rows.length >= MAX_SHOP_FILES)
            throw new ShopRefusal("At most 20 files per product, including variant files. Remove a file and Save before adding another.");
        await client.query("INSERT INTO builder_product_file_slots(site_id,product_id,id) VALUES($1,$2,$3) ON CONFLICT DO NOTHING", [siteId, productId, id]);
    }
    async beginFileUpload(siteId, actor, productId, input) {
        return this.inSite(siteId, actor, async (client) => {
            await this.product(client, siteId, productId);
            const file = parse(ShopFileInputSchema.omit({ sha256: true }), input);
            await this.reserveFile(client, siteId, productId, file.id);
            const id = `upload-${crypto.randomUUID()}`;
            const key = `private/shop/${siteId}/${productId}/uploads/${id}`;
            const uploadId = await this.fileStorage().begin(key, file.contentType);
            await client.query("INSERT INTO builder_product_uploads(site_id,product_id,id,metadata,object_key,upload_id) VALUES($1,$2,$3,$4::jsonb,$5,$6)", [siteId, productId, id, json(file), key, uploadId]);
            return { id, chunkSize: SHOP_FILE_CHUNK_BYTES };
        });
    }
    async uploadSession(client, siteId, productId, id) {
        await this.product(client, siteId, productId);
        parse(ShopIdSchema, id);
        const found = await client.query("SELECT * FROM builder_product_uploads WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, id]);
        if (!found.rows[0])
            throw new ShopRefusal("Upload does not exist");
        if (found.rows[0].state === "cancelled")
            throw new ShopRefusal("Upload was cancelled. Choose the file again.");
        return found.rows[0];
    }
    async uploadFilePart(siteId, actor, productId, id, partNumber, bytes, size = bytes instanceof Uint8Array ? bytes.length : -1) {
        return this.inSite(siteId, actor, async (client) => {
            const upload = await this.uploadSession(client, siteId, productId, id);
            if (upload.state !== "uploading")
                throw new ShopRefusal("This upload is already complete");
            const count = Math.ceil(upload.metadata.size / SHOP_FILE_CHUNK_BYTES);
            if (!Number.isInteger(partNumber) || partNumber < 1 || partNumber > count || size !== Math.min(SHOP_FILE_CHUNK_BYTES, upload.metadata.size - (partNumber - 1) * SHOP_FILE_CHUNK_BYTES))
                throw new ShopRefusal("This file chunk has the wrong size or position");
            const part = await this.fileStorage().part(upload.object_key, upload.upload_id, partNumber, bytes);
            const parts = [...upload.parts.filter((entry) => entry.partNumber !== partNumber), part].sort((a, b) => a.partNumber - b.partNumber);
            await client.query("UPDATE builder_product_uploads SET parts=$4::jsonb WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, id, json(parts)]);
            return part;
        });
    }
    /**
     * Assembles the parts in R2 and hashes the result once, outside any database transaction, so a 2 GB file never
     * holds a Postgres connection open while it streams. The version keeps the object where the upload put it; its
     * key is recorded on the file row, so the bytes are never copied.
     */
    async completeFileUpload(siteId, actor, productId, id) {
        const upload = await this.inSite(siteId, actor, (client) => this.uploadSession(client, siteId, productId, id));
        if (upload.result) {
            const result = await this.inSite(siteId, actor, async (client) => {
                await this.refreshFileRetention(client, siteId, productId, upload.result);
                const original = (await client.query("SELECT object_key FROM builder_product_files WHERE site_id=$1 AND product_id=$2 AND id=$3 AND version=$4", [siteId, productId, upload.result.id, upload.result.version])).rows[0];
                if (original.object_key !== upload.object_key)
                    await client.query("INSERT INTO builder_product_file_gc(site_id,object_key) VALUES($1,$2) ON CONFLICT DO NOTHING", [siteId, upload.object_key]);
                return upload.result;
            });
            await collectFileGarbage(this.db, this.fileStorage());
            return result;
        }
        const count = Math.ceil(upload.metadata.size / SHOP_FILE_CHUNK_BYTES);
        if (upload.parts.length !== count || upload.parts.some((part, i) => part.partNumber !== i + 1))
            throw new ShopRefusal("Upload every file chunk before finishing");
        const storage = this.fileStorage();
        if (upload.state === "uploading") {
            // A retry after R2 assembled the parts, but before the state was saved, finds the multipart upload gone.
            await storage.complete(upload.object_key, upload.upload_id, upload.parts).catch(async (error) => {
                if (await storage.exists(upload.object_key))
                    return;
                // A concurrent completion may already have committed and collected a duplicate key.
                const completed = await this.inSite(siteId, actor, async (client) => (await this.uploadSession(client, siteId, productId, id)).result);
                if (!completed)
                    throw error;
            });
            await this.inSite(siteId, actor, (client) => client.query("UPDATE builder_product_uploads SET state='assembled' WHERE site_id=$1 AND product_id=$2 AND id=$3 AND state='uploading'", [siteId, productId, id]));
        }
        const stored = await storage.inspect(upload.object_key);
        if (!stored) {
            // Another completion may have registered the reused version and collected this duplicate while we hashed.
            const completed = await this.inSite(siteId, actor, async (client) => {
                const again = await this.uploadSession(client, siteId, productId, id);
                if (again.result)
                    await this.refreshFileRetention(client, siteId, productId, again.result);
                return again.result;
            });
            if (completed) {
                await collectFileGarbage(this.db, storage);
                return completed;
            }
        }
        if (!stored || stored.size !== upload.metadata.size || stored.contentType !== upload.metadata.contentType)
            throw new ShopRefusal("The completed file does not match the upload");
        const file = { ...upload.metadata, sha256: stored.sha256 };
        const result = await this.inSite(siteId, actor, async (client) => {
            const again = await this.uploadSession(client, siteId, productId, id);
            if (again.result) {
                await this.refreshFileRetention(client, siteId, productId, again.result);
                return again.result;
            }
            const result = await this.registerFileRow(client, siteId, productId, file, upload.object_key);
            await client.query("UPDATE builder_product_uploads SET state='complete',result=$4::jsonb WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, id, json(result)]);
            return result;
        });
        await collectFileGarbage(this.db, storage);
        return result;
    }
    async cancelFileUpload(siteId, actor, productId, id) {
        await this.inSite(siteId, actor, async (client) => {
            const found = await client.query("SELECT state FROM builder_product_uploads WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, id]);
            if (found.rows[0]?.state === "cancelled")
                return;
            const upload = await this.uploadSession(client, siteId, productId, id);
            if (upload.result)
                return;
            await this.fileStorage().abort(upload.object_key, upload.upload_id).catch(() => undefined);
            await this.fileStorage().remove(upload.object_key);
            await client.query("UPDATE builder_product_uploads SET state='cancelled' WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, id]);
            if (!(await client.query("SELECT 1 FROM builder_product_files WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, upload.metadata.id])).rowCount)
                await client.query("DELETE FROM builder_product_file_slots WHERE site_id=$1 AND product_id=$2 AND id=$3", [siteId, productId, upload.metadata.id]);
        });
    }
    /** Compatibility for small existing clients; larger files use the multipart routes. */
    async uploadFile(siteId, actor, productId, fileId, name, contentType, body) {
        return this.inSite(siteId, actor, async (client) => {
            await this.product(client, siteId, productId);
            if (!this.objects)
                throw new ShopRefusal("File storage is unavailable");
            if (!body.byteLength || body.byteLength > 5 * 1024 * 1024)
                throw new ShopRefusal("Use multipart uploads for files over 5 MB");
            const file = parse(ShopFileInputSchema, { id: fileId, name, contentType, size: body.byteLength, sha256: await hashBytes(body) });
            const key = shopFileObjectKey(siteId, productId, file.id, file.sha256);
            const existing = await this.objects.get(key);
            if (existing && (existing.contentType !== contentType || await hashBytes(existing.body) !== file.sha256))
                throw new ShopRefusal("The stored file doesn't match this upload");
            if (!existing)
                await this.objects.put(key, body, contentType);
            return this.registerFileRow(client, siteId, productId, file, key);
        });
    }
    /** Verifies private bytes by streaming, never loading a large registered version into memory. */
    async registerFile(siteId, actor, productId, input) {
        return this.inSite(siteId, actor, (client) => this.registerFileRow(client, siteId, productId, input));
    }
    /** `verifiedKey` is an object this request has just hashed (a completed multipart upload); other keys are checked here. */
    async registerFileRow(client, siteId, productId, input, verifiedKey) {
        await this.product(client, siteId, productId);
        const file = parse(ShopFileInputSchema, input);
        await this.reserveFile(client, siteId, productId, file.id);
        const objectKey = verifiedKey ?? shopFileObjectKey(siteId, productId, file.id, file.sha256);
        if (verifiedKey) {
            // Hashed by the caller just before this transaction.
        }
        else if (this.objects?.privateFiles) {
            const stored = await this.objects.privateFiles.inspect(objectKey);
            if (!stored || stored.contentType !== file.contentType || stored.size !== file.size || stored.sha256 !== file.sha256)
                throw new ShopRefusal("Private file upload does not match its metadata");
        }
        else {
            const stored = await this.objects?.get(objectKey);
            if (!stored || stored.contentType !== file.contentType || stored.body.byteLength !== file.size || await hashBytes(stored.body) !== file.sha256)
                throw new ShopRefusal("Private file upload does not match its metadata");
        }
        const existing = await client.query("SELECT version,created_at,object_key FROM builder_product_files WHERE site_id=$1 AND product_id=$2 AND id=$3 AND sha256=$4 AND name=$5 AND content_type=$6 AND size=$7", [siteId, productId, file.id, file.sha256, file.name, file.contentType, file.size]);
        if (existing.rows[0]) {
            if (objectKey !== existing.rows[0].object_key)
                await client.query("INSERT INTO builder_product_file_gc(site_id,object_key) VALUES($1,$2) ON CONFLICT DO NOTHING", [siteId, objectKey]);
            await this.refreshFileRetention(client, siteId, productId, { id: file.id, version: existing.rows[0].version });
            return { ...file, productId, version: existing.rows[0].version, createdAt: new Date(existing.rows[0].created_at).toISOString() };
        }
        // A new metadata version may reuse the same immutable bytes. The object key is not a public asset key.
        const counter = await client.query("INSERT INTO builder_product_file_versions(site_id,product_id,id,version) VALUES($1,$2,$3,1) ON CONFLICT(site_id,product_id,id) DO UPDATE SET version=builder_product_file_versions.version+1 RETURNING version", [siteId, productId, file.id]);
        const inserted = await client.query(`INSERT INTO builder_product_files(site_id,product_id,id,version,name,content_type,size,sha256,object_key)
      VALUES($1,$2,$3,$9,$4,$5,$6,$7,$8) RETURNING version,created_at`, [siteId, productId, file.id, file.name, file.contentType, file.size, file.sha256, objectKey, counter.rows[0].version]);
        return { ...file, productId, version: inserted.rows[0].version, createdAt: new Date(inserted.rows[0].created_at).toISOString() };
    }
    /** Called under the site lock when an upload retries or reuses an immutable version. */
    async refreshFileRetention(client, siteId, productId, file) {
        await this.assertFile(client, siteId, productId, file);
        await client.query(`UPDATE builder_product_files SET removed_at=CASE WHEN builder_file_attached(site_id,product_id,id,version)
      THEN NULL ELSE now() END,gc_attempts=0,gc_last_error=NULL WHERE site_id=$1 AND product_id=$2 AND id=$3 AND version=$4`, [siteId, productId, file.id, file.version]);
    }
    newVariantId = () => `variant-${crypto.randomUUID()}`;
    async settings(client, siteId) {
        const found = await client.query("SELECT settings FROM builder_shop WHERE site_id=$1", [siteId]);
        if (!found.rows[0])
            throw new ShopRefusal("Set up the shop before editing its catalogue");
        return found.rows[0].settings;
    }
    async product(client, siteId, id) {
        parse(ShopIdSchema, id);
        const product = await new ShopStore(client).product(siteId, id);
        if (!product)
            throw new ShopRefusal("Product does not exist");
        return product;
    }
    async lock(client, siteId, write) { const product = await this.product(client, siteId, write.id); expectVersion(write.rowVersion, product.rowVersion); return product; }
    async redirect(client, siteId, from, to) {
        const found = await client.query("SELECT redirects FROM builder_shop WHERE site_id=$1", [siteId]);
        const redirects = to ? moveRedirect(found.rows[0].redirects, from, to) : freeRedirect(found.rows[0].redirects, from);
        await client.query("UPDATE builder_shop SET redirects=$2::jsonb WHERE site_id=$1", [siteId, json(redirects)]);
    }
    async bump(client, siteId) { await client.query("UPDATE builder_shop SET version=version+1,updated_at=now() WHERE site_id=$1", [siteId]); }
    async insertRevision(client, siteId, revision) {
        const hash = await digest(revision);
        await client.query("INSERT INTO builder_product_revisions(site_id,digest,description,seo) VALUES($1,$2,$3,$4::jsonb) ON CONFLICT DO NOTHING", [siteId, hash, revision.description, json(revision.seo)]);
        return hash;
    }
    async log(client, siteId, id, action, actor, before, after, undoes = null) {
        await client.query(`INSERT INTO builder_product_changes(site_id,product_id,seq,action,actor,before,after,undoes)
      VALUES($1,$2,(SELECT coalesce(max(seq),0)+1 FROM builder_product_changes WHERE site_id=$1 AND product_id=$2),$3,$4,$5::jsonb,$6::jsonb,$7)`, [siteId, id, action, actor.userId, json(before), json(after), undoes]);
    }
    async assertDetails(client, siteId, id, details) {
        if (["basket", "checkout", "buy", "order"].includes(details.slug))
            throw new ShopRefusal("That address is reserved for checkout. Choose another product address.");
        if ((await client.query("SELECT 1 FROM builder_products WHERE site_id=$1 AND id<>$2 AND slug=$3 AND NOT deleted", [siteId, id, details.slug])).rowCount)
            throw new ShopRefusal("Another product uses that address");
        if (!(await client.query("SELECT 1 FROM builder_product_revisions WHERE site_id=$1 AND digest=$2", [siteId, details.revision])).rowCount)
            throw new ShopRefusal("Save the product description first");
        for (const [ids, table, message] of [[details.categories, "builder_shop_categories", "Category does not exist"], [details.customizationSetIds, "builder_shop_customization_sets", "Customization set does not exist"]]) {
            if (ids.length && (await client.query(`SELECT 1 FROM ${table} WHERE site_id=$1 AND id=ANY($2::text[])`, [siteId, ids])).rowCount !== ids.length)
                throw new ShopRefusal(message);
        }
        for (const photo of details.photos)
            await this.assertPhoto(client, siteId, id, photo);
        for (const file of details.files)
            await this.assertFile(client, siteId, id, file);
    }
    async assertPhoto(client, siteId, id, photo) {
        if (!(await client.query("SELECT 1 FROM builder_product_assets WHERE site_id=$1 AND product_id=$2 AND asset_id=$3 AND version=$4", [siteId, id, photo.assetId, photo.version])).rowCount)
            throw new ShopRefusal("Add this photo to the product first");
    }
    async assertFile(client, siteId, id, file) {
        if (!(await client.query("SELECT 1 FROM builder_product_files WHERE site_id=$1 AND product_id=$2 AND id=$3 AND version=$4", [siteId, id, file.id, file.version])).rowCount) {
            const collected = await client.query("SELECT 1 FROM builder_product_file_versions WHERE site_id=$1 AND product_id=$2 AND id=$3 AND version >= $4", [siteId, id, file.id, file.version]);
            throw new ShopRefusal(collected.rowCount ? "That file version was removed after 30 days unused. Upload it again." : "Add this file to the product first");
        }
    }
    async saveCopy(client, siteId, actor, product, next, action, undoes = null) {
        next = { ...next, details: parse(ProductDetailsSchema, next.details), variants: parse(z.array(ShopVariantSchema).min(1).max(256), next.variants) };
        const ids = (copy) => new Set([...copy.details.files, ...copy.variants.flatMap((variant) => variant.files ?? [])].map((file) => file.id));
        const nextFiles = ids(next);
        // A product saved before the cap keeps its files: refuse only a save that adds a file beyond it.
        const currentFiles = ids(productCopy(product));
        if (nextFiles.size > MAX_SHOP_FILES && [...nextFiles].some((id) => !currentFiles.has(id)))
            throw new ShopRefusal("At most 20 files per product, including variant files");
        if (same(productCopy(product), next))
            return product;
        await this.assertDetails(client, siteId, product.id, next.details);
        const settings = await this.settings(client, siteId);
        for (const variant of next.variants) {
            if (variant.currency !== settings.currency)
                throw new ShopRefusal("Use the shop currency for variant prices");
            if (variant.photo)
                await this.assertPhoto(client, siteId, product.id, variant.photo);
            for (const file of variant.files ?? [])
                await this.assertFile(client, siteId, product.id, file);
        }
        if ((next.details.price === 0 || next.variants.some((variant) => variant.price === 0)) && (next.details.physical || !next.details.downloads))
            throw new ShopRefusal("Free prices are only allowed on download-only products. Enter a price above 0 for a physical item.");
        if (next.availability === "available") {
            const missing = next.variants.filter((variant) => variant.price === null).map((variant) => next.details.options.map((option) => option.values.find((value) => value.id === variant.values[option.id])?.label ?? option.name).join(" · ") || next.details.name);
            if (missing.length)
                throw new ShopRefusal(`Enter a price for: ${missing.join(", ")}.`);
            if (!next.details.physical && !next.details.downloads)
                throw new ShopRefusal("Choose what the buyer gets before making this product available");
            if (next.details.downloads && next.variants.filter((variant) => variant.available).some((variant) => !(variant.files ?? next.details.files).length))
                throw new ShopRefusal("Add download files for every selling variant");
        }
        if (product.availability === "available" && product.details.slug !== next.details.slug)
            await this.redirect(client, siteId, `${settings.base}/${product.details.slug}`, `${settings.base}/${next.details.slug}`);
        if (next.availability === "available")
            await this.redirect(client, siteId, `${settings.base}/${next.details.slug}`);
        const removed = [...ids(productCopy(product))].filter((id) => !nextFiles.has(id));
        if (removed.length)
            await client.query("DELETE FROM builder_product_file_slots WHERE site_id=$1 AND product_id=$2 AND id=ANY($3::text[])", [siteId, product.id, removed]);
        for (const id of nextFiles)
            await this.reserveFile(client, siteId, product.id, id);
        await this.writeVariants(client, siteId, product.id, next.variants);
        await client.query("UPDATE builder_products SET details=$3::jsonb,slug=$4,availability=$5,row_version=row_version+1,updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, product.id, json(next.details), next.details.slug, next.availability]);
        await this.log(client, siteId, product.id, action, actor, productCopy(product), next, undoes);
        if (product.availability === "available" || next.availability === "available")
            await this.bump(client, siteId);
        return this.product(client, siteId, product.id);
    }
    async writeVariants(client, siteId, productId, variants) {
        await client.query("UPDATE builder_product_variants SET active=false WHERE site_id=$1 AND product_id=$2 AND NOT (id=ANY($3::text[]))", [siteId, productId, variants.map((variant) => variant.id)]);
        for (const variant of variants) {
            const existing = await client.query("SELECT product_id,data,active FROM builder_product_variants WHERE site_id=$1 AND id=$2", [siteId, variant.id]);
            if (existing.rows[0] && existing.rows[0].product_id !== productId)
                throw new ShopRefusal("Variant id belongs to another product");
            if (!existing.rows[0])
                await client.query("INSERT INTO builder_product_variants(site_id,id,product_id,data) VALUES($1,$2,$3,$4::jsonb)", [siteId, variant.id, productId, json(variant)]);
            else if (!same(existing.rows[0].data, variant) || !existing.rows[0].active)
                await client.query("UPDATE builder_product_variants SET data=$3::jsonb,active=true,row_version=row_version+1 WHERE site_id=$1 AND id=$2", [siteId, variant.id, json(variant)]);
            await client.query("INSERT INTO builder_variant_stock(site_id,variant_id) VALUES($1,$2) ON CONFLICT DO NOTHING", [siteId, variant.id]);
        }
    }
    async restoreCopy(client, siteId, product, copy) {
        const categories = await client.query("SELECT id FROM builder_shop_categories WHERE site_id=$1", [siteId]);
        const sets = await client.query("SELECT id FROM builder_shop_customization_sets WHERE site_id=$1", [siteId]);
        return { ...copy, availability: product.availability, details: { ...copy.details, categories: copy.details.categories.filter((id) => categories.rows.some((row) => row.id === id)), customizationSetIds: copy.details.customizationSetIds.filter((id) => sets.rows.some((row) => row.id === id)) } };
    }
}
/** A private namespace, isolated by site, product and logical file. Never a public asset route. */
export function shopFileObjectKey(siteId, productId, fileId, sha256) {
    for (const id of [siteId, productId, fileId])
        parse(ShopIdSchema, id);
    parse(z.string().regex(/^[a-f0-9]{64}$/), sha256);
    return `private/shop/${siteId}/${productId}/files/${fileId}/${sha256}`;
}
